// @ts-strict-ignore
import * as React from 'react';
import { EmptyStateWrapper } from '@/components/releaseEmptyState/EmptyStateWrapper';
import {
    AppPlatform,
    AppStoreDefaultSettings,
    AppStoreType,
    AppleAppStoreData,
    AppleAppStoreDefaultSettings,
    GooglePlayStoreData,
    ReleaseStepType,
    AppStoreReviewSubmissionItemState,
    GoogleAppStoreSettings,
    HuaweiAppGallerySettings,
    HuaweiAppStoreData,
    AppStoreBuild,
    RegressionBuildInfo,
    RegressionStatus,
    AppStoreAvailableBuild,
    CIBuildInfo,
    ReleaseCandidateBuildInfo,
    SamsungAppStoreData,
    QAHistoryItem,
    BuildMatchingWarning,
} from '@/types';
import { FeatureSection, Nullable, StepRunComponentProps } from '@/typescriptHelpers';
import {
    appleAppIsReleased,
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    googleAppIsReleased,
    huaweiAppIsSubmitted,
    shouldShowLastUploadedBuilds,
    samsungAppIsSubmitted,
} from '@/helpers';
import AppleAppStoreReleaseSettings from '@/components/AppleAppStoreReleaseSettings';
import AppleReviewInfo from '@/components/AppleReviewInfo';
import { runwayColors, releaseStepModuleTitleStyle } from '@/components/styles/globalStyles';
import { AppStoreItemsCollapse } from '@/components/AppStoreItems/AppStoreItemsCollapse';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import { AppStoreItemsCardHeader } from '@/components/AppStoreItems/AppStoreItemsCardHeader';
import { AndroidAppStoreReleaseSettingsBase } from '@/components/AndroidAppStoreReleaseSettings';
import { SelectedAppStoreBuildModuleEmbedded } from '@/components/SelectedAppStoreBuildModule';
import { LastUploadedAppStoreBuildModuleEmbedded } from '@/components/LastUploadedAppStoreBuildModule';
import { HuaweiAppStoreReleaseSettingsBase } from '@/components/HuaweiAppStoreReleaseSettings';
import { Space } from 'antd/lib';
import Text from 'antd/lib/typography/Text';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';
import AgeRatingWarning from '../AgeRatingWarning';
import { AgeRatingSummary } from '@/types/ageRating';

export interface BuildAndReviewSettingsCollapseProps {
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType: ReleaseStepType;
    appStoreData: AppleAppStoreData;
    googleStoreData: GooglePlayStoreData;
    huaweiStoreData: HuaweiAppStoreData;
    samsungStoreData: SamsungAppStoreData;
    appStoreType: AppStoreType;
    platform: AppPlatform;
    appStoreReleaseSettings: AppStoreDefaultSettings;
    isRollback: boolean;
    onShowAppStoreReleaseSettings: () => void;
    storeUrl: string;
    isReleased: boolean;
    lastUploadedBuild: AppStoreBuild;
    state?: AppStoreReviewSubmissionItemState;
    regressionBuildInfo?: RegressionBuildInfo;
    regressionStatus?: RegressionStatus;
    appStoreAvailableBuilds: Nullable<AppStoreAvailableBuild[]>;
    retainedBuildsManuallySet?: boolean;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    additionalAppStoreSelectedBuilds: Nullable<string[]>;
    latestSuccessfulProdBuild: Nullable<CIBuildInfo>;
    latestSuccessfulBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    autoStagedRolloutAutomationId: Nullable<string>;
    latestPassingRegressionTestBuild: Nullable<QAHistoryItem>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    isAppStoreBuildListFilteredByConfiguredTrack?: boolean;
    buildMatchingWarnings?: BuildMatchingWarning[];
    showAgeRatingWarning?: boolean;
    ageRating?: Nullable<AgeRatingSummary>;
}

export const BuildAndReviewSettingsCard: React.FC<BuildAndReviewSettingsCollapseProps> = ({
    orgId,
    appId,
    releaseStepType,
    releaseId,
    stepRunComponentProps,
    appStoreData,
    googleStoreData,
    huaweiStoreData,
    samsungStoreData,
    appStoreType,
    platform,
    appStoreReleaseSettings,
    isRollback,
    onShowAppStoreReleaseSettings,
    storeUrl,
    isReleased,
    lastUploadedBuild,
    state,
    regressionBuildInfo,
    regressionStatus,
    appStoreAvailableBuilds,
    retainedBuildsManuallySet,
    appStoreSelectedBuilds,
    additionalAppStoreSelectedBuilds,
    latestSuccessfulProdBuild,
    latestSuccessfulBuild,
    selectedStagingBuild,
    autoStagedRolloutAutomationId,
    latestPassingRegressionTestBuild,
    isAppStoreBuildListFilteredByConfiguredTrack,
    buildMatchingWarnings,
    showAgeRatingWarning,
    ageRating,
}) => {
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);
    const shouldShowLastUploadedBuildComponent = shouldShowLastUploadedBuilds(appStoreSelectedBuilds, appStoreType);
    const [isReviewDataModalOpen, setIsReviewDataModalOpen] = React.useState(false);

    return (
        <AppStoreItemsCollapse
            header={
                <EmptyStateWrapper
                    releaseStepType={releaseStepType}
                    appId={appId}
                    releaseId={releaseId}
                    featureSections={[FeatureSection.SubmissionSelectedBuildModule]}
                    shouldForceDisable={appStoreSelectedBuilds?.length > 0 || !!lastUploadedBuild}
                >
                    {state && <AppStoreItemsCardHeader state={state} />}
                    {shouldShowLastUploadedBuildComponent ?
                        <LastUploadedAppStoreBuildModuleEmbedded
                            appStoreBuild={lastUploadedBuild}
                            appStoreType={appStoreType}
                            releaseStepType={releaseStepType}
                            releaseId={releaseId}
                            appId={appId}
                            orgId={orgId}
                        />
                    :   <SelectedAppStoreBuildModuleEmbedded
                            stepRunComponentProps={stepRunComponentProps}
                            orgId={orgId}
                            appId={appId}
                            releaseId={releaseId}
                            releaseStepType={releaseStepType}
                            platform={platform}
                            appStoreType={appStoreType}
                            appStoreState={
                                appStoreData?.versionReviewSubmissionItem?.state ??
                                googleStoreData?.state ??
                                huaweiStoreData?.state ??
                                samsungStoreData?.state ??
                                null
                            }
                            googleReleaseLifecycleState={googleStoreData?.releaseLifecycleState}
                            showChangeSelectedBuildCTA={!getIsHuaweiAppGallery(appStoreType)}
                            regressionBuildInfo={regressionBuildInfo}
                            regressionStatus={regressionStatus}
                            appStoreAvailableBuilds={appStoreAvailableBuilds}
                            appStoreSelectedBuilds={appStoreSelectedBuilds}
                            additionalAppStoreSelectedBuilds={additionalAppStoreSelectedBuilds}
                            retainedBuildsManuallySet={retainedBuildsManuallySet}
                            latestSuccessfulProdBuild={latestSuccessfulProdBuild}
                            latestSuccessfulBuild={latestSuccessfulBuild}
                            selectedStagingBuild={selectedStagingBuild}
                            latestPassingRegressionTestBuild={latestPassingRegressionTestBuild}
                            isAppStoreBuildListFilteredByConfiguredTrack={isAppStoreBuildListFilteredByConfiguredTrack}
                            buildMatchingWarnings={buildMatchingWarnings}
                        />
                    }
                </EmptyStateWrapper>
            }
            collapsibleItems={
                isAppleAppStore ?
                    [
                        {
                            header: (
                                <Space>
                                    <Text style={{ ...releaseStepModuleTitleStyle }}>
                                        App Store Connect release settings
                                    </Text>
                                    <TooltipInfoIcon tooltipContent="Current release settings in App Store Connect" />
                                </Space>
                            ),
                            content: (
                                <EmptyStateWrapper
                                    releaseStepType={releaseStepType}
                                    appId={appId}
                                    releaseId={releaseId}
                                    featureSections={[FeatureSection.SubmissionAppStoreReleaseSettings]}
                                    shouldForceDisable={!!appStoreReleaseSettings}
                                >
                                    {showAgeRatingWarning && (
                                        <div style={{ marginBottom: 16 }}>
                                            <AgeRatingWarning
                                                orgId={orgId}
                                                appId={appId}
                                                stepConfigId={stepRunComponentProps?.stepConfigId}
                                            />
                                        </div>
                                    )}
                                    <AppleAppStoreReleaseSettings
                                        appId={appId}
                                        releaseId={releaseId}
                                        orgId={orgId}
                                        appStoreState={appStoreData?.versionReviewSubmissionItem?.state}
                                        appStoreReleaseSettings={
                                            appStoreReleaseSettings as AppleAppStoreDefaultSettings
                                        }
                                        appStoreData={appStoreData}
                                        isRollback={isRollback}
                                        onShowAppStoreReleaseSettings={onShowAppStoreReleaseSettings}
                                        stepRunComponentProps={stepRunComponentProps}
                                        releaseStepType={releaseStepType}
                                        ageRating={ageRating}
                                    />
                                </EmptyStateWrapper>
                            ),
                            extra: (isExpanded: boolean) => {
                                const editReleaseSettingsDisabled =
                                    appStoreData?.versionReviewSubmissionItem?.state ?
                                        appleAppIsReleased(appStoreData?.versionReviewSubmissionItem?.state)
                                    :   !isRollback;

                                return isExpanded ?
                                        <PermissionedActionButton
                                            releaseId={releaseId}
                                            actionType={RunwayUserAction.UAEditAppStoreReleaseSettings}
                                            icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                            onClick={onShowAppStoreReleaseSettings}
                                            disabled={editReleaseSettingsDisabled}
                                        >
                                            Edit release settings
                                        </PermissionedActionButton>
                                    :   null;
                            },
                        },
                        {
                            header: 'App review information',
                            content: (
                                <EmptyStateWrapper
                                    releaseStepType={releaseStepType}
                                    appId={appId}
                                    releaseId={releaseId}
                                    featureSections={[FeatureSection.ReviewAppStoreReview]}
                                    shouldForceDisable={!!appStoreData?.reviewDetail}
                                >
                                    <AppleReviewInfo
                                        stepRunComponentProps={stepRunComponentProps}
                                        {...appStoreData?.reviewDetail}
                                        storeUrl={storeUrl}
                                        isDefaultCollapsed={true}
                                        releaseId={releaseId}
                                        isModalOpen={isReviewDataModalOpen}
                                        setIsModalOpen={setIsReviewDataModalOpen}
                                        appId={appId}
                                        orgId={orgId}
                                    />
                                </EmptyStateWrapper>
                            ),
                            extra: (isExpanded: boolean) => {
                                const ctaDisabled = appStoreData ? isReleased : true;

                                return isExpanded ?
                                        <>
                                            <PermissionedActionButton
                                                releaseId={releaseId}
                                                actionType={RunwayUserAction.UAEditAppStoreReleaseSettings}
                                                icon={<ArrowRightOutlined style={{ color: runwayColors.info }} />}
                                                onClick={() => setIsReviewDataModalOpen(true)}
                                                disabled={ctaDisabled}
                                            >
                                                Edit review info
                                            </PermissionedActionButton>
                                        </>
                                    :   null;
                            },
                        },
                    ]
                : isGooglePlayStore ?
                    [
                        {
                            header: 'Google Play release settings',
                            content: (
                                <EmptyStateWrapper
                                    releaseStepType={releaseStepType}
                                    appId={appId}
                                    releaseId={releaseId}
                                    featureSections={[FeatureSection.SubmissionAppStoreReleaseSettings]}
                                    shouldForceDisable={!!appStoreReleaseSettings}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
                                        <AndroidAppStoreReleaseSettingsBase
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            isReleaseRollback={isRollback}
                                            releaseSettings={appStoreReleaseSettings as GoogleAppStoreSettings}
                                            storeUrl={storeUrl}
                                            autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                            onShowAppStoreReleaseSettings={onShowAppStoreReleaseSettings}
                                            stepRunComponentProps={stepRunComponentProps}
                                        />
                                    </div>
                                </EmptyStateWrapper>
                            ),
                            extra: (isExpanded: boolean) => {
                                const editReleaseSettingsDisabled =
                                    googleStoreData?.state ? googleAppIsReleased(googleStoreData?.state) : !isRollback;

                                return isExpanded ?
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAUpdateAppSettings}
                                            icon={
                                                <EditOutlined
                                                    style={{ color: runwayColors.info }}
                                                    disabled={editReleaseSettingsDisabled}
                                                />
                                            }
                                            onClick={onShowAppStoreReleaseSettings}
                                            releaseId={releaseId}
                                        >
                                            Edit release settings
                                        </PermissionedActionButton>
                                    :   null;
                            },
                        },
                    ]
                : isHuaweiAppGallery ?
                    [
                        {
                            header: 'AppGallery release settings',
                            content: (
                                <EmptyStateWrapper
                                    releaseStepType={releaseStepType}
                                    appId={appId}
                                    releaseId={releaseId}
                                    featureSections={[FeatureSection.SubmissionAppStoreReleaseSettings]}
                                    shouldForceDisable={!!appStoreReleaseSettings}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 12px' }}>
                                        <HuaweiAppStoreReleaseSettingsBase
                                            orgId={orgId}
                                            appId={appId}
                                            autoStagedRolloutAutomationId={autoStagedRolloutAutomationId}
                                            releaseId={releaseId}
                                            releaseSettings={appStoreReleaseSettings as HuaweiAppGallerySettings}
                                            storeUrl={storeUrl}
                                            onShowAppStoreReleaseSettings={onShowAppStoreReleaseSettings}
                                        />
                                    </div>
                                </EmptyStateWrapper>
                            ),
                            extra: (isExpanded: boolean) => {
                                const editReleaseSettingsDisabled =
                                    huaweiStoreData?.state ? huaweiAppIsSubmitted(huaweiStoreData?.state)
                                    : samsungStoreData?.state ? samsungAppIsSubmitted(samsungStoreData?.state)
                                    : false;

                                return isExpanded ?
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAUpdateAppSettings}
                                            icon={
                                                <EditOutlined
                                                    style={{ color: runwayColors.info }}
                                                    disabled={editReleaseSettingsDisabled}
                                                />
                                            }
                                            onClick={onShowAppStoreReleaseSettings}
                                            releaseId={releaseId}
                                        >
                                            Edit release settings
                                        </PermissionedActionButton>
                                    :   null;
                            },
                        },
                    ]
                :   []
            }
        />
    );
};
